import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '@services/api';
import { apiErrorMessage } from '@utils/apiError';
import type { MonetizationTab } from '@/types';
import {
  callsToMake,
  initialMonetizationState,
  monetizationReducer,
  queryFor,
} from './monetizationTabState';
import type {
  MonetizationAction,
  MonetizationFilters,
  MonetizationState,
  PageTab,
} from './monetizationTabState';

export interface MonetizationController {
  state: MonetizationState;
  activeTab: PageTab;
  openTab(tab: PageTab): void;
  setFilters(filters: MonetizationFilters): void;
  /** Asks again for the open tab's failed calls. */
  retry(): void;
}

/**
 * Drives the Monetization page: each tab is fetched when it is first opened (Banners & Offers
 * makes two calls), a switch back makes no call, a change of the controls clears the tabs whose
 * query changed and the open tab refetches, and a stale answer is dropped. The data is daily and
 * cached 15 minutes by the backend, so there is no auto-refresh.
 *
 * Calls are tracked per tab with a token (see monetizationReducer) rather than with one shared
 * useLatestRequest counter, which would drop the answer of a tab fetched at the same time as
 * another. The state is mirrored in a ref so two calls in one tick (React StrictMode runs the
 * mount effect twice) cannot both see a tab as unfetched.
 */
export function useMonetizationTab(initialFilters: MonetizationFilters): MonetizationController {
  const stateRef = useRef<MonetizationState>(initialMonetizationState(initialFilters));
  const [state, setState] = useState<MonetizationState>(stateRef.current);
  const [activeTab, setActiveTab] = useState<PageTab>('summary');
  const activeRef = useRef<PageTab>('summary');
  const tokenRef = useRef(0);

  const apply = useCallback((action: MonetizationAction) => {
    stateRef.current = monetizationReducer(stateRef.current, action);
    setState(stateRef.current);
  }, []);

  const ensureLoaded = useCallback(
    (tab: PageTab) => {
      for (const call of callsToMake(stateRef.current, tab)) {
        const token = ++tokenRef.current;
        apply({ type: 'start', tab: call, token });
        const params = queryFor(call as MonetizationTab, stateRef.current.filters);
        adminApi
          .getMonetization<unknown>(call, params)
          .then((res) => apply({ type: 'loaded', tab: call, token, result: res.data }))
          .catch((err: unknown) => {
            console.error(`Failed to load Monetization ${call}`, err);
            apply({
              type: 'failed',
              tab: call,
              token,
              message: apiErrorMessage(err, `Failed to load Monetization ${call}.`),
            });
          });
      }
    },
    [apply]
  );

  useEffect(() => {
    ensureLoaded('summary');
  }, [ensureLoaded]);

  const openTab = useCallback(
    (tab: PageTab) => {
      activeRef.current = tab;
      setActiveTab(tab);
      ensureLoaded(tab);
    },
    [ensureLoaded]
  );

  const setFilters = useCallback(
    (filters: MonetizationFilters) => {
      apply({ type: 'filters', filters });
      ensureLoaded(activeRef.current);
    },
    [apply, ensureLoaded]
  );

  const retry = useCallback(() => ensureLoaded(activeRef.current), [ensureLoaded]);

  return { state, activeTab, openTab, setFilters, retry };
}
